import {
  addDays,
  endOfMonth,
  format,
  getISOWeek,
  getISOWeekYear,
  parseISO,
  startOfISOWeek,
} from "date-fns";
import { ko } from "date-fns/locale";

export const DATE_FORMAT = "yyyy-MM-dd";
const DAYS_IN_WEEK = 7;
const ISO_WEEK_DIGITS = 2;

export const today = (): string => format(new Date(), DATE_FORMAT);

export const shiftDate = (date: string, days: number): string =>
  format(addDays(parseISO(date), days), DATE_FORMAT);

export const weekStartOf = (date: string): string =>
  format(startOfISOWeek(parseISO(date)), DATE_FORMAT);

export const weekDates = (weekStart: string): string[] =>
  Array.from({ length: DAYS_IN_WEEK }, (_, offset) => shiftDate(weekStart, offset));

export const dayLabel = (date: string): string =>
  format(parseISO(date), "yyyy-MM-dd (EEE)", { locale: ko });

export const weekdayLabel = (date: string): string =>
  format(parseISO(date), "EEEEE", { locale: ko });

/**
 * 「10월 1일 (수) – 20일 (토)」 — 기간 한 줄.
 *
 * 같은 달이면 뒤쪽의 달을 적지 않는다. 좁은 상세 창에서 기간이 두 줄로 접히는 것을 막으려는
 * 것이고, 「10월 1일 – 10월 20일」처럼 같은 말을 두 번 하지 않는 쪽이 읽기도 낫다.
 * 하루짜리면 한 날만 적는다.
 */
export const dateSpan = (start: string, end: string): string => {
  const day = (date: string) => format(parseISO(date), "M월 d일 (EEE)", { locale: ko });
  if (start === end) return day(start);
  const sameMonth = start.slice(0, "yyyy-MM".length) === end.slice(0, "yyyy-MM".length);
  const tail = sameMonth ? format(parseISO(end), "d일 (EEE)", { locale: ko }) : day(end);
  return `${day(start)} – ${tail}`;
};

export const isWeekend = (date: string): boolean => [0, 6].includes(parseISO(date).getDay());

/**
 * 그 사이의 평일 수(양 끝 포함) — 토·일을 뺀다.
 *
 * 사무소가 주 5일로 일을 세기 때문이다. 하루 몫을 든 투입 계획에 이 수를 곱하면 그 기간의
 * 시간이 나온다 — 달력일로 세면 주말에도 시간이 붙는다.
 */
export const workdays = (from: string, to: string): number => {
  let count = 0;
  for (let day = parseISO(from); day <= parseISO(to); day = addDays(day, 1))
    if (![0, 6].includes(day.getDay())) count += 1;
  return count;
};

export const isoWeekLabel = (date: string): string => {
  const parsed = parseISO(date);
  return `${getISOWeekYear(parsed)}-W${String(getISOWeek(parsed)).padStart(ISO_WEEK_DIGITS, "0")}`;
};

/**
 * 「2026년 10월 5주차」 — 그 주(월요일)의 목요일이 속한 해·달과 그 달의 몇 번째 목요일인지(ISO 주 기준).
 * 근태와 Weekly 의 머리에 쓴다.
 */
export const monthWeekLabel = (weekStart: string): string => {
  const thursday = parseISO(shiftDate(weekStart, 3));
  return `${thursday.getFullYear()}년 ${thursday.getMonth() + 1}월 ${Math.ceil(thursday.getDate() / 7)}주차`;
};

export const weekKey = (weekStart: string): string =>
  `${format(parseISO(weekStart), "yyMMdd")}WEEK`;

/** 그 달의 1일 */
export const monthStartOf = (date: string): string => `${date.slice(0, 7)}-01`;

/** 달력에 그리는 주들의 월요일 — 그 달 1일이 든 주부터 말일이 든 주까지(4~6주). */
export function monthWeeks(monthStart: string): string[] {
  const last = format(endOfMonth(parseISO(monthStart)), DATE_FORMAT);
  const weeks: string[] = [];
  for (let w = weekStartOf(monthStart); w <= last; w = shiftDate(w, 7)) weeks.push(w);
  return weeks;
}
